import { Article } from '@ninjacart/nc-nk-core';
import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  Card,
  Image,
  Input,
  KisanLogoNew,
  Paragraph,
  Separator,
  Text,
  useTheme,
  useTimeAgo,
  View,
  XStack,
  YStack,
} from '@ninjacart/nc-nk-ui';
import { useRouter } from '@ninjacart/nc-nk-web-deps';
import { FlashList, ViewToken } from '@shopify/flash-list';
import { ChevronLeft, Volume2 } from '@tamagui/lucide-icons';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, LayoutRectangle, Platform, Pressable } from 'react-native';
// import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Link } from 'solito/link';
import {
  fetchAgriNewsList,
  KissanEventType,
  useAgriNewsStore,
  useAppPersistenceStore,
  useEventZStore,
} from '@ninjacart/nc-nk-store';
import dynamic from 'next/dynamic';
import AgriNewsListItem from './AgriNewsListItem';

// Removed inline NewsRow; now using extracted component

export const AgriNews = ({
  articles,
  initialMaxId,
}: {
  articles: Article[];
  initialMaxId: string;
}) => {
  // const insets = useSafeAreaInsets();
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const processEvents = useRef(useQueuedEventStore((state) => state.processEvents)).current;
  const [searchQuery, setSearchQuery] = useState<string>();
  const [newslist, setNewsList] = useState<Article[] | null>(articles);
  const [searchResults, setSearchResults] = useState<Article[] | null>(articles);
  const [loading, setLoading] = useState(false);
  const [layout, setLayout] = useState<LayoutRectangle | null>(null);
  const textInputRef = useRef<Input>(null);
  const theme = useTheme();
  const { push } = useRouter();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const agriNewsList = useAgriNewsStore((state) => state.agriNewsList);
  const setAgriNewsList = useAgriNewsStore((state) => state.setAgriNewsList);
  const resetAgriNewsList = useAgriNewsStore((state) => state.resetAgriNewsList);
  const maxIdRef = useRef<string | undefined>(initialMaxId);
  const enableAudioListenerPlatforms = useEventZStore.getState()?.enableAudioListenerPlatforms;
  const audioPlayerRefs = useRef<{ [key: number]: any }>({});
  const flashListRef = useRef<FlashList<any>>(null);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);
  const [currentPlayIndex, setCurrentPlayIndex] = useState(-1);
  const uniqueDeviceId = useAppPersistenceStore((state) => state.deviceId);

  /** Auto scroll + auto play next */
  const handleAutoScroll = (currentIndex: number) => {
    const nextIndex = currentIndex + 1;
    const data = searchResults ?? newslist ?? [];

    if (nextIndex < data.length) {
      const newsScrolledEvent: INinjaEvent = {
        name: KissanEventType.NEWS_SCROLLED_NEXT,
        screenDetails: { screen: 'agriNews page' },
        eventParams: [
          addEventParam('scrolledIndex', nextIndex.toString()),
          addEventParam('url', data[currentIndex]?.source_url ?? data[currentIndex]?.link),
          addEventParam('type', 'agriNews'),
          addEventParam('date', data[currentIndex]?.publishedAt ?? data[currentIndex]?.pubDate),
          addEventParam('selectedCategory', (data[currentIndex]?.category ?? []).toString()),
        ],
      };
      enqueue(newsScrolledEvent);
      // Use the actual flashListRef
      flashListRef.current?.scrollToIndex({ index: nextIndex, animated: true });
      setCurrentPlayIndex(nextIndex);

      // Give some time for scroll animation
      // setTimeout(() => {
      //   audioPlayerRefs.current[nextIndex]?.autoStartPlayback?.();
      // }, 800);
    }
  };

  const newsLoadSuccessEvent = async () => {
    const searchResultEvent: INinjaEvent = {
      name: KissanEventType.AGRINEWS_LOAD_SUCCESS,
      screenDetails: { screen: 'agriNews page' },
    };
    await processEvents([searchResultEvent]);
  };

  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 100,
    minimumViewTime: 1000,
  }).current;

  useEffect(() => {
    if (articles?.length) {
      setAgriNewsList(articles);
    }
    return () => {
      resetAgriNewsList();
    };
  }, []);

  useEffect(() => {
    if (uniqueDeviceId && articles?.length) {
      newsLoadSuccessEvent();
    }
  }, [uniqueDeviceId, articles?.length]);

  const handleOnEndReached = async () => {
    if (isFetchingMore) return;
    setIsFetchingMore(true);
    try {
      const result = await fetchAgriNewsList(maxIdRef.current);
      const newArticles = result?.news || [];
      maxIdRef.current = result?.maxId ?? '';
      if (newArticles.length > 0) {
        setNewsList((prev) => [...(prev ?? []), ...newArticles]);
        const updatedList = [...agriNewsList, ...newArticles];
        setAgriNewsList(updatedList);
      }
    } catch (err) {
      console.error('Error fetching more articles:', err);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const redirectToDetail = useCallback(
    (item: Article, index: number) => {
      setCurrentPlayIndex(-1);
      push(`/newsDetail/${item?.id}`);

      const newsClickedEvent: INinjaEvent = {
        name: KissanEventType.AGRINEWSLINK_CLICK,
        screenDetails: { screen: 'agriNews page' },
        eventParams: [
          addEventParam('itemIndex', index.toString()),
          addEventParam('url', item?.source_url),
          addEventParam('title', item?.title ?? ''),
          addEventParam('itemId', item?.id ?? ''),
          addEventParam('selectedCategory', (item?.category ?? []).toString()),
        ],
      };
      enqueue(newsClickedEvent);
    },
    [push, enqueue]
  );

  const captureImpressionEvent = (index: number, item: Article) => {
    const dataViewedMoreThanOneSecEvent: INinjaEvent = {
      name: KissanEventType.DATA_VIEWED_MORETHAN_ONE_SECOND,
      screenDetails: { screen: 'agriNews page' },
      eventParams: [
        addEventParam('scrolledIndex', index),
        addEventParam('url', item?.source_url ?? item?.link),
        addEventParam('type', 'agriNews'),
        addEventParam('date', item?.publishedAt ?? item?.pubDate),
        addEventParam('title', item?.title ?? ''),
        addEventParam('itemId', item?.id ?? ''),
      ],
    };
    enqueue(dataViewedMoreThanOneSecEvent);
  };

  const onViewableItemsChanged = ({
    viewableItems,
  }: {
    viewableItems: ViewToken[];
    changed: ViewToken[];
  }) => {
    viewableItems.forEach((item) => {
      const index = item.index;
      const itemData = item.item as Article;

      if (index != null) {
        captureImpressionEvent(index, itemData);
      }
    });
  };

  const renderItem = useCallback(
    ({ item, index }: { item: Article; index: number }) => {
      return (
        <AgriNewsListItem
          item={item}
          index={index}
          isActive={currentPlayIndex === index}
          onPressDetail={redirectToDetail}
          currentPlayIndex={currentPlayIndex}
          onAudioAutoScroll={handleAutoScroll}
          setCurrentPlayIndex={setCurrentPlayIndex}
        />
      );
    },
    [currentPlayIndex, redirectToDetail]
  );

  const handleSearchFocus = () => {
    const searchClickEvent: INinjaEvent = {
      name: KissanEventType.AGRINEWS_SEARCH_CLICK,
      screenDetails: { screen: 'agriNews page' },
    };
    processEvents([searchClickEvent]);
  };

  useEffect(() => {
    const delay = setTimeout(async () => {
      if (searchQuery?.trim()) {
        setNewsList([]);
        setLoading(true);

        const results = articles.filter(
          (article) =>
            article.title.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
            article.description.toLowerCase().includes(searchQuery.trim().toLowerCase())
        );
        const categories = results.flatMap((article) => article.category || []);
        const uniqueCategories = [...new Set(categories)];

        setSearchResults(results);
        setLoading(false);
        const searchResultEvent: INinjaEvent = {
          name: KissanEventType.AGRINEWS_SEARCH_ITEMS,
          screenDetails: { screen: 'agriNews page' },
          eventParams: [
            addEventParam('searchedText', searchQuery),
            addEventParam('selectedCategory', (uniqueCategories ?? []).toString()),
          ],
        };
        processEvents([searchResultEvent]);
      } else {
        setNewsList(articles);
        setSearchResults(null);
      }
    }, 800);

    return () => clearTimeout(delay);
  }, [searchQuery]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const searchInput = textInputRef.current?.['value'];
      setSearchQuery(searchInput);
    }
  };

  const onGoBack = (e: any) => {
    console.log('On go back');
    push('/');
  };

  return (
    <YStack
      flex={1}
      backgroundColor="$background"
      // paddingHorizontal={16}
      onLayout={(e) => {
        setLayout(e.nativeEvent.layout);
      }}
      fullscreen
    >
      <YStack margin={8}>
        <XStack height={48} onPress={(e) => push('/')}>
          <YStack
            testID="kisan_tId_BackButton"
            justifyContent="flex-start"
            alignItems="flex-start"
            top={12}
            // paddingLeft={12}
            flex={1}
            height={48}
            
          >
            <ChevronLeft size="2" color={theme['color']?.val} />
          </YStack>
          <YStack flex={12} maxHeight={48}>
            <KisanLogoNew width={140} height={48} />
          </YStack>
        </XStack>

        <XStack
          backgroundColor="#fff7d1"
          padding={4}
          borderRadius={8}
          alignItems="center"
          space="$2"
          marginBottom={4}
        >
          {/* Volume icon */}
          <Volume2 color="black" size={18} testID="kisan_tId_mute" />

          {/* Instruction text */}
          <Text fontSize={14} fontWeight={600} color="#555" flex={1}>
            कृपया नीचे दिखाए गए माइक बटन पर क्लिक करें ताकि आप समाचार सुनना शुरू कर सकें।
          </Text>
        </XStack>

        <Input
          ref={textInputRef}
          placeholder="🔍 तलाश करें: गेहूं की खेती के लिए सुझाव?"
          backgroundColor="#f1ede6"
          borderRadius={12}
          marginBottom={8}
          height={48}
          placeholderTextColor="#8d8373"
          onFocus={handleSearchFocus}
          returnKeyType="done"
          onKeyPress={handleKeyDown}
          color={'#000'}
        />

        <Text fontSize={24} gap={'$4'} fontWeight="bold" paddingHorizontal={12}>
          {searchQuery ?? 'कृषि'}
        </Text>

        {loading && (
          <YStack alignItems="center" marginTop={12}>
            <ActivityIndicator size="large" color="#999" /> Loading....
          </YStack>
        )}
      </YStack>

      <YStack flex={1}>
        {layout && (
          <FlashList
            ref={flashListRef}
            data={searchResults ?? newslist}
            renderItem={renderItem}
            viewabilityConfig={viewabilityConfig}
            onViewableItemsChanged={onViewableItemsChanged}
            showsVerticalScrollIndicator={false}
            onEndReached={handleOnEndReached}
            onEndReachedThreshold={0.7}
            ListFooterComponent={
              isFetchingMore ? (
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ActivityIndicator size={50} color="#fff" />
                </View>
              ) : null
            }
            keyExtractor={(item) => item?.id}
            estimatedItemSize={150}
            contentContainerStyle={{ paddingBottom: 90, paddingTop: 8 }}
            ListEmptyComponent={
              <Paragraph color="#999" marginTop={32} textAlign="center">
                कोई खबर नहीं मिली 🔍
              </Paragraph>
            }
            extraData={currentPlayIndex}
            ItemSeparatorComponent={Separator}
          />
        )}
      </YStack>
    </YStack>
  );
};
